/**
 * Générateur — Calculer la valeur de formules à l'aide d'une suite
 * d'instruction dans un langage de programmation par blocs.
 * Attendu ID : 999 | Niveau : 5ème | Domaine : La pensée informatique
 *
 * Libellé officiel (spec 999, 2026-09-21) : « Calculer la valeur de formules à
 * l'aide d'une suite d'instruction dans un langage de programmation par
 * blocs. »
 *
 * Plan de cartes (mise en conformité spécification, 2026-09-21) :
 *
 *   Carte « completer » — Compléter le programme. QCM à choix IMAGES : un
 *   programme par blocs (squelette : les données posées par des affectations,
 *   l'affectation du résultat vide, le bloc « dire »), l'élève choisit le bloc
 *   qui va dans « mettre <résultat> à ». La palette des variables est listée
 *   dans l'énoncé, avec une ou deux variables leurres. Quatre niveaux tirés à
 *   égalité (formules connues : aire et périmètre du carré, périmètre du
 *   triangle équilatéral, heures en minutes ; aire et périmètre du rectangle,
 *   aire du triangle rectangle ; remise et taxe en deux étapes ; fréquence à
 *   dénominateur somme).
 *
 *   Carte « verifier » — Le programme calcule-t-il vraiment ? Tableau V/F :
 *   trois programmes côte à côte (le bon, et deux fautifs parmi : résultat en
 *   dur, nombres de l'énoncé au lieu des variables, affichage absent ou d'une
 *   autre variable), une affirmation par programme ; les affirmations sur le
 *   jeu de valeurs de CONTRÔLE (les mêmes données avec d'autres valeurs)
 *   démasquent le résultat en dur. Niveaux 1 et 2.
 *
 *   Carte « deux_etapes » — Remise ou taxe en deux étapes. Texte à trous en
 *   glisser-déposer : les deux affectations (montant, puis prix final) à
 *   compléter avec des blocs proposés en étiquettes. Niveau 3.
 *
 *   Carte « valeur » (« Que vaut ma variable ? », QCM d'écriture + saisie de la
 *   valeur d'un bloc « mettre ma variable à … ») : SUPPRIMÉE. Elle demandait à
 *   l'élève de calculer la valeur (restriction : « c'est le programme qui
 *   calcule, pas l'élève à la main » ; exclusion « aucun programme validé sur
 *   le seul examen de sa valeur finale ») et ne portait aucune formule connue,
 *   aucune donnée, aucune variable de résultat, aucun affichage.
 *
 * Spécification appliquée :
 *
 *   Bornes : 1 à 2 données ; 2 à 4 variables (données, intermédiaire
 *   éventuelle, résultat) ; donnée géométrique entière dans [3 ; 16] ; prix
 *   entier multiple de 10 dans [20 ; 120] ; taux dans {5, 10, 20, 25, 30, 40}
 *   avec un montant entier ; 1 à 2 variables leurres dans la palette ; une
 *   question par item ; jeu de valeurs de contrôle tiré avec l'énoncé, valeurs
 *   et résultat différents.
 *   Exclusions : aucune boucle, aucun test, aucune saisie utilisateur (ni
 *   « demander » ni « réponse »), aucune formule inconnue, résultats à deux
 *   décimales au plus, aucune validation sur la seule valeur finale (la
 *   valeur n'est jamais demandée à l'élève).
 *
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code) :
 *     1. écrire le résultat en dur           → resultat_en_dur
 *     2. les nombres de l'énoncé au lieu des variables
 *                                            → nombres_au_lieu_des_variables
 *     3. le montant de la remise au lieu du prix après remise
 *                                            → montant_au_lieu_du_prix
 *     4. ajouter là où il faut retrancher (remise / taxe)
 *                                            → signe_remise_taxe
 *     5. choisir une variable leurre         → variable_leurre
 *     6. omettre le bloc d'affichage ou afficher autre chose
 *                                            → affichage_absent_ou_faux
 *     7. oublier d'emboîter l'addition dans la division
 *                                            → somme_non_emboitee_dans_division
 *
 *   Graduation → cartes :
 *     niveau 1 (une donnée, une opération)          : completer, verifier
 *     niveau 2 (deux données, une ou deux opérations, leurre) : completer, verifier
 *     niveau 3 (deux étapes, variable intermédiaire) : completer, deux_etapes
 *     niveau 4 (dénominateur somme, résultat non entier) : completer
 *
 *   Non tranché (voir audit/5eme/999.md) : le type_reponse prescrit un éditeur
 *   de blocs avec double exécution ; ici QCM / V/F / glisser-déposer, la
 *   double exécution étant portée par les affirmations du V/F ; aucune borne
 *   pour les heures ni pour les effectifs de la fréquence (bornes reprises :
 *   [3 ; 16] pour les heures, effectif total dans {5, 10, 20, 25, 50} pour que
 *   la fréquence ait au plus deux décimales).
 */

import {
    randomChoice, randInt, shuffle, makeQcm, makeVraiFaux, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';
import { imgElement, imgChoix, COULEURS_BLOCS, fmtFr } from './svgScratch';

const ATTENDU_ID = 999;

const L_COMPLETER = 'Compléter le programme';
const L_VERIFIER = 'Le programme calcule-t-il vraiment ?';
const L_DEUX_ETAPES = 'Remise ou taxe en deux étapes';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Bornes de la spécification ──────────────────────────────

const GEO_MIN = 3, GEO_MAX = 16;                  // valeur_d_une_donnee_geometrique
const PRIX_MIN = 20, PRIX_MAX = 120;              // valeur_d_une_donnee_monetaire (multiple de 10)
const TAUX = [5, 10, 20, 25, 30, 40];             // taux_en_pourcentage
const TOTAUX_FREQUENCE = [5, 10, 20, 25, 50];     // effectif total : fréquence à 2 décimales au plus

// ─── Arbres d'expression : nombre | { v: nom } | { trou: true } | { op, a, b } ──

const V = (nom) => ({ v: nom });
const TROU = { trou: true };
const noeud = (op, a, b) => ({ op, a, b });
const estOp = (n) => typeof n === 'object' && n !== null && n.op !== undefined;

const evaluer = (n, valeurs) => {
    if (typeof n === 'number') return n;
    if (n.v !== undefined) {
        if (valeurs[n.v] === undefined) throw new Error(`gen_999 : variable ${n.v} sans valeur`);
        return valeurs[n.v];
    }
    const a = evaluer(n.a, valeurs), b = evaluer(n.b, valeurs);
    switch (n.op) {
        case '+': return a + b;
        case '−': return a - b;
        case '×': return a * b;
        default: return a / b;
    }
};

/** Clé structurelle (unicité des choix). */
const cle = (n) => {
    if (typeof n === 'number') return fmtFr(n);
    if (n.trou) return '?';
    if (n.v !== undefined) return `[${n.v}]`;
    return `(${cle(n.a)}${n.op}${cle(n.b)})`;
};

/** Écriture texte (étiquettes du glisser-déposer) : parenthèses quand l'emboîtement contredit les priorités. */
const PRIO = { '+': 1, '−': 1, '×': 2, '/': 2 };
const ecrire = (n) => {
    if (typeof n === 'number') return fmtFr(n);
    if (n.v !== undefined) return n.v;
    const g = ecrire(n.a), d = ecrire(n.b), p = PRIO[n.op];
    const parA = estOp(n.a) && PRIO[n.a.op] < p;
    const parB = estOp(n.b) && (PRIO[n.b.op] < p || (PRIO[n.b.op] === p && (n.op === '−' || n.op === '/')));
    return `${parA ? `(${g})` : g} ${n.op === '/' ? '÷' : n.op} ${parB ? `(${d})` : d}`;
};

// ─── Rendu SVG local : blocs avec expressions à variables ────
// (svgScratch ne rend pas de variable nommée en ovale orange : rendu local,
// même palette, mêmes cotes ; un seul <svg>, jamais d'imbrication.)

const FS = 13;
const BH = 30, GAP = 4, EH = 24, PAD = 7, W_OP = 18;
const OPS = { '+': '+', '−': '−', '×': '×', '/': '/' };
const largeurTexte = (txt) => Math.ceil(String(txt).length * 6.9) + 4;
const rect = (x, y, w, h, fill, rx = 6) =>
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${rx}" fill="${fill}" stroke="#0f172a" stroke-opacity="0.25" stroke-width="1"/>`;
const txt = (x, y, s, o = {}) =>
    `<text x="${x}" y="${y}" font-size="${o.size || FS}" fill="${o.fill || '#fff'}"${o.milieu ? ' text-anchor="middle"' : ''}${o.gras ? ' font-weight="bold"' : ''} font-family="Arial">${s}</text>`;

const construireExpr = (n) => {
    if (typeof n === 'number' || n.trou) {
        const s = n.trou ? '?' : fmtFr(n);
        const w = largeurTexte(s) + 14;
        return { w, h: EH, frag: (x, y) => rect(x, y, w, EH, '#ffffff', EH / 2)
            + txt(x + w / 2, y + EH / 2 + 5, s, { fill: '#0f172a', milieu: true, gras: !!n.trou }) };
    }
    if (n.v !== undefined) {
        const w = largeurTexte(n.v) + 16;
        return { w, h: EH, frag: (x, y) => rect(x, y, w, EH, COULEURS_BLOCS.variable, EH / 2)
            + txt(x + w / 2, y + EH / 2 + 5, n.v, { milieu: true }) };
    }
    const a = construireExpr(n.a), b = construireExpr(n.b);
    const h = Math.max(a.h, b.h) + 2 * PAD;
    const w = PAD + a.w + W_OP + b.w + PAD;
    return { w, h, frag: (x, y) => rect(x, y, w, h, COULEURS_BLOCS.op, h / 2)
        + a.frag(x + PAD, y + (h - a.h) / 2)
        + txt(x + PAD + a.w + W_OP / 2, y + h / 2 + 5, OPS[n.op], { milieu: true, gras: true, size: FS + 1 })
        + b.frag(x + PAD + a.w + W_OP, y + (h - b.h) / 2) };
};

const enveloppe = (frag, w, h) =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w + 12} ${h + 12}" width="${w + 12}" height="${h + 12}" font-family="Arial">`
    + `<rect x="0" y="0" width="${w + 12}" height="${h + 12}" fill="#eef2f7" rx="8"/><g transform="translate(6,6)">${frag}</g></svg>`;

/** Un bloc d'expression seul (choix de QCM). */
const svgExpression = (n) => {
    const e = construireExpr(n);
    return enveloppe(e.frag(0, 0), e.w, e.h);
};

/**
 * Pile de blocs : { t: 'hat', txt } | { t: 'variable'|'looks', prefixe, expr }.
 * Retourne un fragment positionné en (0, 0) et ses dimensions.
 */
const construireProgramme = (blocs) => {
    const items = blocs.map((b) => ({ b, e: b.expr !== undefined ? construireExpr(b.expr) : null }));
    const w = Math.max(...items.map(({ b, e }) => (e ? 10 + largeurTexte(b.prefixe) + 8 + e.w + 10 : largeurTexte(b.txt) + 24)));
    let y = 0, frag = '';
    items.forEach(({ b, e }) => {
        const h = e ? Math.max(BH, e.h + 12) : BH;
        if (b.t === 'hat') {
            frag += `<path d="M 0 ${y + h} v ${-(h - 12)} q 0 -12 14 -12 h 28 q 10 -9 20 0 h ${w - 76} q 14 0 14 12 v ${h - 12} z" fill="${COULEURS_BLOCS.hat}" stroke="#0f172a" stroke-opacity="0.25" stroke-width="1"/>`;
        } else frag += rect(0, y, w, h, COULEURS_BLOCS[b.t]);
        frag += txt(10, y + h / 2 + 5, e ? b.prefixe : b.txt);
        if (e) frag += e.frag(10 + largeurTexte(b.prefixe) + 8, y + (h - e.h) / 2);
        y += h + GAP;
    });
    return { frag, w, h: y - GAP };
};

const svgProgramme = (blocs) => {
    const p = construireProgramme(blocs);
    return enveloppe(p.frag, p.w, p.h);
};

const svgProgrammes = (progs) => {
    const mT = 22;
    const cols = progs.map(({ titre, blocs }) => ({ titre, ...construireProgramme(blocs) }));
    const H = mT + Math.max(...cols.map((c) => c.h));
    let x = 0, frag = '';
    cols.forEach((c) => {
        frag += txt(x, 14, c.titre, { fill: '#1e293b', gras: true })
            + `<g transform="translate(${x},${mT})">${c.frag}</g>`;
        x += Math.max(c.w, largeurTexte(c.titre)) + 24;
    });
    return enveloppe(frag, x - 24, H);
};

// ─── Formules connues (variable formule_a_programmer) ────────
// Chaque formule : niveau, données (noms), résultat, leurres, tirage des
// valeurs, expression(x) où x(nom) rend la variable OU sa valeur, expression
// avec leurre, et l'intitulé du contexte.

const g = () => randInt(GEO_MIN, GEO_MAX);

/** Le groupe nominal accordé pour chaque grandeur produite (aire féminin, périmètre masculin). */
const GROUPE_RESULTAT = {
    aire: 'la bonne aire',
    'périmètre': 'le bon périmètre',
    minutes: 'le bon nombre de minutes',
};

const FORMULES = [
    {
        id: 'aire_carre', niveau: 1, donnees: ['côté'], resultat: 'aire', leurres: ['largeur', 'périmètre'],
        tirer: () => ({ 'côté': g() }),
        expr: (x) => noeud('×', x('côté'), x('côté')),
        leurre: (x, l) => noeud('×', x('côté'), V(l)),
        quoi: (d) => `l'aire d'un carré de côté ${d['côté']}`,
    },
    {
        id: 'perimetre_carre', niveau: 1, donnees: ['côté'], resultat: 'périmètre', leurres: ['aire', 'largeur'],
        tirer: () => ({ 'côté': g() }),
        expr: (x) => noeud('×', x('côté'), 4),
        leurre: (x, l) => noeud('×', V(l), 4),
        quoi: (d) => `le périmètre d'un carré de côté ${d['côté']}`,
    },
    {
        id: 'perimetre_triangle_equilateral', niveau: 1, donnees: ['côté'], resultat: 'périmètre', leurres: ['hauteur', 'aire'],
        tirer: () => ({ 'côté': g() }),
        expr: (x) => noeud('×', x('côté'), 3),
        leurre: (x, l) => noeud('×', V(l), 3),
        quoi: (d) => `le périmètre d'un triangle équilatéral de côté ${d['côté']}`,
    },
    {
        id: 'heures_minutes', niveau: 1, donnees: ['heures'], resultat: 'minutes', leurres: ['secondes', 'jours'],
        tirer: () => ({ heures: g() }),
        expr: (x) => noeud('×', x('heures'), 60),
        leurre: (x, l) => noeud('×', V(l), 60),
        quoi: (d) => `le nombre de minutes dans ${d.heures} heures`,
    },
    {
        id: 'aire_rectangle', niveau: 2, donnees: ['longueur', 'largeur'], resultat: 'aire', leurres: ['hauteur', 'périmètre'],
        tirer: () => { const l = randInt(GEO_MIN, GEO_MAX - 1); return { longueur: randInt(l + 1, GEO_MAX), largeur: l }; },
        expr: (x) => noeud('×', x('longueur'), x('largeur')),
        leurre: (x, l) => noeud('×', x('longueur'), V(l)),
        quoi: (d) => `l'aire d'un rectangle de longueur ${d.longueur} et de largeur ${d.largeur}`,
    },
    {
        id: 'perimetre_rectangle', niveau: 2, donnees: ['longueur', 'largeur'], resultat: 'périmètre', leurres: ['hauteur', 'aire'],
        tirer: () => { const l = randInt(GEO_MIN, GEO_MAX - 1); return { longueur: randInt(l + 1, GEO_MAX), largeur: l }; },
        expr: (x) => noeud('×', noeud('+', x('longueur'), x('largeur')), 2),
        leurre: (x, l) => noeud('×', noeud('+', x('longueur'), V(l)), 2),
        quoi: (d) => `le périmètre d'un rectangle de longueur ${d.longueur} et de largeur ${d.largeur}`,
    },
    {
        id: 'aire_triangle_rectangle', niveau: 2, donnees: ['base', 'hauteur'], resultat: 'aire', leurres: ['côté', 'périmètre'],
        tirer: () => { const b = g(); let h = g(); if (h === b) h = b === GEO_MAX ? b - 1 : b + 1; return { base: b, hauteur: h }; },
        expr: (x) => noeud('/', noeud('×', x('base'), x('hauteur')), 2),
        leurre: (x, l) => noeud('/', noeud('×', x('base'), V(l)), 2),
        quoi: (d) => `l'aire d'un triangle rectangle dont les côtés de l'angle droit mesurent ${d.base} et ${d.hauteur}`,
    },
];

/** Prix et taux tels que le montant soit entier. */
const tirerPrixTaux = () => {
    for (let i = 0; i < 60; i++) {
        const prix = randInt(PRIX_MIN / 10, PRIX_MAX / 10) * 10, taux = randomChoice(TAUX);
        if (Number.isInteger((prix * taux) / 100)) return { prix, taux };
    }
    return { prix: 80, taux: 25 };
};

const CONTEXTES_FREQUENCE = [
    { a: 'filles', b: 'garçons', leurre: 'absents', quoi: (d) => `la fréquence des filles dans une classe de ${d.filles} filles et ${d.garçons} garçons` },
    { a: 'rouges', b: 'bleues', leurre: 'vertes', quoi: (d) => `la fréquence des billes rouges dans un sac de ${d.rouges} billes rouges et ${d.bleues} billes bleues` },
];

/** Fréquence part ÷ (part + reste) : total dans TOTAUX_FREQUENCE, part et reste ≥ 2. */
const tirerFrequence = () => {
    const total = randomChoice(TOTAUX_FREQUENCE);
    const part = randInt(2, total - 2);
    return { part, reste: total - part };
};

const listePalette = (noms) => shuffle(noms).join(', ');

// ═════════ Carte 1 — Compléter le programme (QCM d'images, niveaux 1 à 4) ═════════

const BLOC_HAT = { t: 'hat', txt: 'quand le drapeau est cliqué' };
const affecter = (nom, expr) => ({ t: 'variable', prefixe: `mettre ${nom} à`, expr });
const dire = (nom) => ({ t: 'looks', prefixe: 'dire', expr: V(nom) });

/** Assemble le QCM d'images à partir de la bonne expression et des candidats codés. */
const qcmImages = (bonne, candidats, texte, params) => {
    const vus = new Set([cle(bonne)]);
    const distracteurs = shuffle(candidats).filter((c) => {
        const k = cle(c.arbre);
        if (vus.has(k)) return false;
        vus.add(k);
        return true;
    }).slice(0, 3);
    if (distracteurs.length < 2) throw new Error('gen_999 : moins de deux distracteurs distincts');
    const choixCorrect = imgChoix(svgExpression(bonne));
    const payload = makeQcm([choixCorrect, ...distracteurs.map((d) => imgChoix(svgExpression(d.arbre)))], choixCorrect, texte, opts(L_COMPLETER));
    payload._meta.params = { ...params, distracteurs: distracteurs.map((d) => ({ code: d.code, ecriture: ecrire(d.arbre) })) };
    return payload;
};

const genCompleterNiveau12 = (niveau) => {
    const f = randomChoice(FORMULES.filter((x) => x.niveau === niveau));
    const d = f.tirer();
    const leurres = shuffle(f.leurres).slice(0, randInt(1, 2));
    const bonne = f.expr(V);
    const valeur = evaluer(bonne, d);
    const blocs = [BLOC_HAT, ...f.donnees.map((n) => affecter(n, d[n])), affecter(f.resultat, TROU), dire(f.resultat)];
    const texte = `<p><b>Ce programme doit afficher ${f.quoi(d)}. Quel bloc faut-il placer dans « mettre ${f.resultat} à » ?</b></p>`
        + `<p>Variables disponibles : ${listePalette([...f.donnees, f.resultat, ...leurres])}.</p>`
        + `<p>Rappel : ${f.resultat} = ${ecrire(bonne)}.</p>`;
    const candidats = [
        { code: 'resultat_en_dur', arbre: valeur },
        { code: 'nombres_au_lieu_des_variables', arbre: f.expr((n) => d[n]) },
        { code: 'variable_leurre', arbre: f.leurre(V, leurres[0]) },
    ];
    const payload = qcmImages(bonne, candidats, texte, {
        niveau, formule: f.id, donnees: d, nb_donnees: f.donnees.length, nb_variables: f.donnees.length + 1,
        leurres, valeur, ecriture: ecrire(bonne),
    });
    payload.elements_additionnels = imgElement(svgProgramme(blocs));
    return payload;
};

const genCompleterNiveau3 = () => {
    const remise = Math.random() < 0.5;
    const { prix, taux } = tirerPrixTaux();
    const montant = (prix * taux) / 100;
    const final = remise ? prix - montant : prix + montant;
    const etape = randInt(1, 2);
    const leurres = shuffle(remise ? ['remise', 'total'] : ['TVA', 'total']).slice(0, randInt(1, 2));
    const exprMontant = (x) => noeud('/', noeud('×', x('prix'), x('taux')), 100);
    const exprFinal = (x) => noeud(remise ? '−' : '+', x('prix'), x('montant'));
    const d = { prix, taux, montant };
    const bonne = etape === 1 ? exprMontant(V) : exprFinal(V);
    const blocs = [
        BLOC_HAT, affecter('prix', prix), affecter('taux', taux),
        affecter('montant', etape === 1 ? TROU : exprMontant(V)),
        affecter('prix final', etape === 2 ? TROU : exprFinal(V)),
        dire('prix final'),
    ];
    const situation = remise
        ? `le prix après une remise de ${taux} % sur un article à ${prix} €`
        : `le prix toutes taxes comprises d'un article à ${prix} € hors taxes, la taxe étant de ${taux} %`;
    const texte = `<p><b>Ce programme doit afficher ${situation}. Quel bloc faut-il placer dans `
        + `« mettre ${etape === 1 ? 'montant' : 'prix final'} à » ?</b></p>`
        + `<p>Variables disponibles : ${listePalette(['prix', 'taux', 'montant', 'prix final', ...leurres])}.</p>`
        + `<p>Rappel : montant = prix × taux ÷ 100, puis prix final = prix ${remise ? '−' : '+'} montant.</p>`;
    const candidats = etape === 1
        ? [
            { code: 'resultat_en_dur', arbre: montant },
            { code: 'nombres_au_lieu_des_variables', arbre: exprMontant((n) => d[n]) },
            { code: 'variable_leurre', arbre: noeud('/', noeud('×', V('prix'), V(leurres[0])), 100) },
        ]
        : [
            { code: 'resultat_en_dur', arbre: final },
            { code: 'nombres_au_lieu_des_variables', arbre: exprFinal((n) => d[n]) },
            { code: 'signe_remise_taxe', arbre: noeud(remise ? '+' : '−', V('prix'), V('montant')) },
            { code: 'montant_au_lieu_du_prix', arbre: V('montant') },
        ];
    const payload = qcmImages(bonne, candidats, texte, {
        niveau: 3, formule: remise ? 'prix_apres_remise' : 'prix_ttc', etape, donnees: { prix, taux },
        nb_donnees: 2, nb_variables: 4, leurres, montant, valeur: final, ecriture: ecrire(bonne),
    });
    payload.elements_additionnels = imgElement(svgProgramme(blocs));
    return payload;
};

const genCompleterNiveau4 = () => {
    const c = randomChoice(CONTEXTES_FREQUENCE);
    const { part, reste } = tirerFrequence();
    const d = { [c.a]: part, [c.b]: reste };
    const leurres = [c.leurre];
    const expr = (x) => noeud('/', x(c.a), noeud('+', x(c.a), x(c.b)));
    const bonne = expr(V);
    const valeur = evaluer(bonne, d);
    const blocs = [BLOC_HAT, affecter(c.a, part), affecter(c.b, reste), affecter('fréquence', TROU), dire('fréquence')];
    const texte = `<p><b>Ce programme doit afficher ${c.quoi(d)}. Quel bloc faut-il placer dans « mettre fréquence à » ?</b></p>`
        + `<p>Variables disponibles : ${listePalette([c.a, c.b, 'fréquence', ...leurres])}.</p>`
        + `<p>Rappel : fréquence = ${c.a} ÷ (${c.a} + ${c.b}), l'effectif total est la somme.</p>`;
    const candidats = [
        { code: 'somme_non_emboitee_dans_division', arbre: noeud('+', noeud('/', V(c.a), V(c.a)), V(c.b)) },
        { code: 'resultat_en_dur', arbre: valeur },
        { code: 'nombres_au_lieu_des_variables', arbre: expr((n) => d[n]) },
        { code: 'variable_leurre', arbre: noeud('/', V(c.a), noeud('+', V(c.a), V(c.leurre))) },
    ];
    const payload = qcmImages(bonne, candidats, texte, {
        niveau: 4, formule: 'frequence', donnees: d, nb_donnees: 2, nb_variables: 3, leurres, valeur, ecriture: ecrire(bonne),
    });
    payload.elements_additionnels = imgElement(svgProgramme(blocs));
    return payload;
};

const genCompleterQcm = () => {
    const niveau = randInt(1, 4);
    if (niveau <= 2) return genCompleterNiveau12(niveau);
    return niveau === 3 ? genCompleterNiveau3() : genCompleterNiveau4();
};

// ═════════ Carte 2 — Le programme calcule-t-il vraiment ? (V/F, niveaux 1-2) ═════════

const KINDS_FAUTIFS = ['resultat_en_dur', 'nombres_au_lieu_des_variables', 'affichage_faux', 'affichage_absent'];
const CODE_KIND = {
    ok: null,
    resultat_en_dur: 'resultat_en_dur',
    nombres_au_lieu_des_variables: 'nombres_au_lieu_des_variables',
    affichage_faux: 'affichage_absent_ou_faux',
    affichage_absent: 'affichage_absent_ou_faux',
};

const blocsProgramme = (f, d, kind) => {
    const bonne = f.expr(V);
    const affectation = kind === 'resultat_en_dur' ? evaluer(bonne, d)
        : kind === 'nombres_au_lieu_des_variables' ? f.expr((n) => d[n]) : bonne;
    const blocs = [BLOC_HAT, ...f.donnees.map((n) => affecter(n, d[n])), affecter(f.resultat, affectation)];
    if (kind === 'affichage_faux') blocs.push(dire(f.donnees[0]));
    else if (kind !== 'affichage_absent') blocs.push(dire(f.resultat));
    return blocs;
};

/** Jeu de contrôle : mêmes données, autres valeurs, résultat différent. */
const tirerControle = (f, d) => {
    const v = evaluer(f.expr(V), d);
    for (let i = 0; i < 60; i++) {
        const c = f.tirer();
        if (f.donnees.every((n) => c[n] !== d[n]) && evaluer(f.expr(V), c) !== v) return c;
    }
    throw new Error('gen_999 : jeu de contrôle introuvable');
};

const listerValeurs = (f, d) => f.donnees.map((n) => `${n} à ${d[n]}`).join(' et ');

const genVerifierVf = () => {
    const niveau = randInt(1, 2);
    const f = randomChoice(FORMULES.filter((x) => x.niveau === niveau));
    const d = f.tirer();
    const c = tirerControle(f, d);
    const valeur = evaluer(f.expr(V), d), valeurC = evaluer(f.expr(V), c);
    const kinds = shuffle(['ok', ...shuffle(KINDS_FAUTIFS).slice(0, 2)]);
    const lettres = ['A', 'B', 'C'];
    const progs = kinds.map((k, i) => ({ lettre: lettres[i], kind: k }));
    // gabarits d'affirmation : vrai(kind)
    const GABARITS = [
        { id: 'toute_valeur', vrai: (k) => k === 'ok',
            // « périmètre » est masculin, « aire » féminin : l'article s'accorde, sinon la ligne
            // affiche « la bonne périmètre » (constat au dump, 2026-09-22).
            texte: (L) => `Le programme ${L} affiche ${GROUPE_RESULTAT[f.resultat]} quelles que soient les valeurs mises dans ${f.donnees.join(' et ')}.` },
        { id: 'valeurs_enonce', vrai: (k) => ['ok', 'resultat_en_dur', 'nombres_au_lieu_des_variables'].includes(k),
            texte: (L) => `Avec ${listerValeurs(f, d)}, le programme ${L} affiche bien ${fmtFr(valeur)}.` },
        { id: 'valeurs_controle', vrai: (k) => k === 'ok',
            texte: (L) => `Si on met ${listerValeurs(f, c)}, le programme ${L} affiche bien ${fmtFr(valeurC)}.` },
        { id: 'affiche_resultat', vrai: (k) => k !== 'affichage_faux' && k !== 'affichage_absent',
            texte: (L) => `Le programme ${L} affiche la valeur de ${f.resultat}.` },
    ];
    let choix;
    for (let i = 0; i < 40; i++) {
        choix = progs.map((p) => ({ ...p, gabarit: randomChoice(GABARITS) }));
        const verites = choix.map((x) => x.gabarit.vrai(x.kind));
        if (verites.includes(true) && verites.includes(false)) break;
        choix = null;
    }
    if (!choix) {
        // repli : le bon programme sur « toute valeur » (vrai), un fautif sur le contrôle (faux)
        choix = progs.map((p) => ({ ...p, gabarit: p.kind === 'ok' ? GABARITS[0] : GABARITS[2] }));
    }
    const lignes = choix.map((x) => x.gabarit.texte(x.lettre));
    const bonnes = choix.map((x) => (x.gabarit.vrai(x.kind) ? 0 : 1));
    const texte = `<p><b>Trois programmes doivent afficher ${f.quoi(d)}. Vrai ou faux ?</b></p>`
        + `<p>Rappel : ${f.resultat} = ${ecrire(f.expr(V))}.</p>`;
    const payload = makeVraiFaux(lignes, bonnes, texte, {
        ...opts(L_VERIFIER),
        elements_additionnels: imgElement(svgProgrammes(progs.map((p) => ({ titre: `Programme ${p.lettre}`, blocs: blocsProgramme(f, d, p.kind) })))),
    });
    payload._meta.params = {
        niveau, formule: f.id, donnees: d, controle: c, nb_donnees: f.donnees.length, nb_variables: f.donnees.length + 1,
        valeur, valeur_controle: valeurC, programmes: progs.map((p) => p.kind),
        gabarits: choix.map((x) => ({ lettre: x.lettre, kind: x.kind, gabarit: x.gabarit.id, vrai: x.gabarit.vrai(x.kind) })),
        distracteurs: choix.filter((x) => !x.gabarit.vrai(x.kind)).map((x) => ({ code: CODE_KIND[x.kind] || 'resultat_en_dur', gabarit: x.gabarit.id })),
    };
    return payload;
};

// ═════════ Carte 3 — Remise ou taxe en deux étapes (glisser-déposer, niveau 3) ═════════

const genDeuxEtapesDrag = () => {
    const remise = Math.random() < 0.5;
    const { prix, taux } = tirerPrixTaux();
    const montant = (prix * taux) / 100;
    const final = remise ? prix - montant : prix + montant;
    const leurre = remise ? 'remise' : 'TVA';
    const etape1 = 'prix × taux ÷ 100';
    const etape2 = remise ? 'prix − montant' : 'prix + montant';
    const phrase = `mettre montant à {{${etape1}}}, puis mettre prix final à {{${etape2}}}.`;
    const candidats = [
        { code: 'signe_remise_taxe', mot: remise ? 'prix + montant' : 'prix − montant' },
        { code: 'montant_au_lieu_du_prix', mot: 'montant' },
        { code: 'nombres_au_lieu_des_variables', mot: `${prix} × ${taux} ÷ 100` },
        { code: 'resultat_en_dur', mot: fmtFr(final) },
        { code: 'variable_leurre', mot: `prix × ${leurre} ÷ 100` },
    ];
    const retenus = shuffle(candidats).slice(0, 4);
    const distracteurs = filtrerDistracteurs(phrase, retenus.map((c) => c.mot));
    const blocs = [BLOC_HAT, affecter('prix', prix), affecter('taux', taux), affecter('montant', TROU), affecter('prix final', TROU), dire('prix final')];
    const situation = remise
        ? `le prix après une remise de ${taux} % sur un article à ${prix} €`
        : `le prix toutes taxes comprises d'un article à ${prix} € hors taxes, la taxe étant de ${taux} %`;
    const texte = `<p><b>Ce programme doit afficher ${situation}. Complète les deux affectations avec les blocs proposés.</b></p>`
        + `<p>Variables disponibles : ${listePalette(['prix', 'taux', 'montant', 'prix final', leurre])}.</p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_DEUX_ETAPES), mode: 'drag', distracteurs, elements_additionnels: imgElement(svgProgramme(blocs)),
    });
    payload._meta.params = {
        niveau: 3, formule: remise ? 'prix_apres_remise' : 'prix_ttc', donnees: { prix, taux }, nb_donnees: 2, nb_variables: 4,
        leurres: [leurre], montant, valeur: final,
        distracteurs: retenus.filter((c) => distracteurs.includes(c.mot)).map((c) => ({ code: c.code, mot: c.mot })),
    };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ───────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'la valeur du résultat n\'est jamais demandée à l\'élève : c\'est le programme qui calcule (restriction de la spec)',
        'chaque distracteur porte son code dans _meta.params.distracteurs ; jamais de bouche-trou',
        'la palette listée dans l\'énoncé contient 1 ou 2 variables leurres qui n\'entrent dans aucune formule',
    ],
    completer_qcm_unique: 'QCM d\'IMAGES (blocs d\'expression) : resultat_en_dur (une pastille), nombres_au_lieu_des_variables (la formule avec les nombres de l\'énoncé), variable_leurre ; au niveau 3 signe_remise_taxe et montant_au_lieu_du_prix ; au niveau 4 somme_non_emboitee_dans_division',
    verifier_tableau_vf: 'trois programmes (le bon + deux fautifs), une affirmation par programme tirée parmi quatre gabarits ; les affirmations sur le jeu de contrôle démasquent resultat_en_dur et nombres_au_lieu_des_variables ; affichage_absent_ou_faux sur le gabarit « affiche la valeur de »',
    deux_etapes_texte_trou_drag: 'étiquettes : les deux bonnes + 4 tirées parmi signe_remise_taxe, montant_au_lieu_du_prix, nombres_au_lieu_des_variables, resultat_en_dur, variable_leurre (filtrées par filtrerDistracteurs)',
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    completer_qcm_unique: genCompleterQcm,
    verifier_tableau_vf: genVerifierVf,
    deux_etapes_texte_trou_drag: genDeuxEtapesDrag,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', texte_trou_drag: 'Drag' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'completer', label: L_COMPLETER, formatsList: buildFormats('completer') },
    { id: 'verifier', label: L_VERIFIER, formatsList: buildFormats('verifier') },
    { id: 'deux_etapes', label: L_DEUX_ETAPES, formatsList: buildFormats('deux_etapes') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
